import React, { useState, useEffect, useMemo, useCallback } from 'react'
import SidePanel from '@/components/ui/SidePanel'
import DataGrid from '@/components/data/DataGrid'
import { designTokens } from '@/utils/design-tokens'
import { TaskService } from '@/api/generated/services/TaskService'
import { TaskItemService } from '@/api/generated/services/TaskItemService'
import { TaskBudgetService } from '@/api/generated/services/TaskBudgetService'
import { TaskPerformanceService } from '@/api/generated/services/TaskPerformanceService'
import type { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'
import type { ColDef } from 'ag-grid-community'
import { useAppSelector } from '@/store/hooks'

interface BusinessDetailPanelProps {
  isOpen: boolean
  onClose: () => void
  businessName?: string
  selectedListType?: 'task' | 'work' | 'budget' | 'performance'
  departmentId?: string // For filtering by department when coming from department-specific detail
}

// Business statistics data interface
interface BusinessStat {
  id: string
  businessName: string
  total: number
  completed: number
  incomplete: number
  progressRate: number
  // For budget calculations (amounts instead of counts)
  totalAmount?: number
  completedAmount?: number
  incompleteAmount?: number
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const BusinessDetailPanel: React.FC<BusinessDetailPanelProps> = ({
  isOpen,
  onClose,
  businessName = '',
  selectedListType = 'task',
  departmentId
}) => {
  // Redux state
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Component state
  const [businessStats, setBusinessStats] = useState<BusinessStat[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Get task type label for display
  const getTaskTypeLabel = () => {
    switch (selectedListType) {
      case 'task':
        return '세부실행과제'
      case 'work':
        return '수행업무'
      case 'budget':
        return '예산'
      case 'performance':
        return '성과'
      default:
        return '세부실행과제'
    }
  }

  // Fetch business-level data (tasks, task items, budgets, or performances)
  const fetchBusinessData = useCallback(async () => {
    if (!selectedWorkspace?.id || !['task', 'work', 'budget', 'performance'].includes(selectedListType || '')) {
      setBusinessStats([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const PER_PAGE = 1000
      const baseParams: any = {
        workspaceId: selectedWorkspace.id,
        perPage: PER_PAGE,
      }
      if (departmentId) {
        baseParams.departmentId = departmentId
      }

      // 선택된 유형에 맞는 페이지 조회 함수 (모든 서비스가 workspaceId 카멜케이스 사용)
      const fetchPage = (page: number): Promise<PaginatedResponse> => {
        const params = { ...baseParams, page }
        switch (selectedListType) {
          case 'task':
            return TaskService.appsTaskViewsListTasks(params)
          case 'work':
            return TaskItemService.appsTaskViewsListTaskItems(params)
          case 'budget':
            return TaskBudgetService.appsTaskViewsListTaskBudgets(params)
          case 'performance':
            return TaskPerformanceService.appsTaskViewsListTaskPerformances(params)
          default:
            return Promise.resolve({
              data: [],
              meta: { page: 1, per_page: PER_PAGE, total: 0, pages: 0, has_next: false, has_prev: false }
            })
        }
      }

      // 첫 페이지 조회 후, per_page를 초과하면(pages > 1) 나머지 페이지도 모두 조회해 합침
      const firstPage = await fetchPage(1)
      const allItems: any[] = [...(firstPage.data || [])]
      const totalPages = firstPage.meta?.pages || 1

      if (totalPages > 1) {
        const restPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2))
        )
        restPages.forEach(res => {
          if (res?.data) allItems.push(...res.data)
        })
      }

      {
        // Transform API data to business statistics
        const businessMap = new Map<string, {
          name: string
          total: number
          completed: number
          incomplete: number
          totalAmount?: number
          completedAmount?: number
          incompleteAmount?: number
        }>()

        // Group data by business and calculate statistics
        allItems.forEach((item: any) => {
          let businessName = ''
          let isCompleted = false

          if (selectedListType === 'task') {
            businessName = item.business_name || '미분류'
            isCompleted = item.status === 'completed'
          } else if (selectedListType === 'work') {
            // For task items, use the business_name field (now available in TaskItemResponse)
            businessName = item.business_name || '미분류'
            isCompleted = ['completed', 'reviewed'].includes(item.status)
          } else if (selectedListType === 'budget' || selectedListType === 'performance') {
            // For budgets and performances, use business_name and status fields
            businessName = item.business_name || '미분류'
            isCompleted = item.status === 'completed'
          }

          if (!businessMap.has(businessName)) {
            businessMap.set(businessName, {
              name: businessName,
              total: 0,
              completed: 0,
              incomplete: 0,
              totalAmount: 0,
              completedAmount: 0,
              incompleteAmount: 0
            })
          }

          const business = businessMap.get(businessName)!

          if (selectedListType === 'budget') {
            // For budget, calculate amounts instead of counts
            const targetAmount = parseFloat(item.items_target_figure) || 0
            const doneAmount = parseFloat(item.items_done_figure) || 0
            const incompleteAmount = targetAmount - doneAmount

            business.totalAmount! += targetAmount
            business.completedAmount! += doneAmount
            business.incompleteAmount! += Math.max(0, incompleteAmount)

            // Keep counts for backward compatibility
            business.total += 1
            if (isCompleted) {
              business.completed += 1
            } else {
              business.incomplete += 1
            }
          } else {
            // For non-budget types, use count-based calculation
            business.total += 1
            if (isCompleted) {
              business.completed += 1
            } else {
              business.incomplete += 1
            }
          }
        })

        // Convert to BusinessStat array
        const stats: BusinessStat[] = Array.from(businessMap.values()).map((business, index) => {
          let progressRate = 0

          if (selectedListType === 'budget') {
            // For budget, calculate progress based on amounts
            progressRate = business.totalAmount! > 0 ? Math.round((business.completedAmount! / business.totalAmount!) * 100) : 0
          } else {
            // For other types, calculate progress based on counts
            progressRate = business.total > 0 ? Math.round((business.completed / business.total) * 100) : 0
          }

          return {
            id: `business-${index}`,
            businessName: business.name,
            total: business.total,
            completed: business.completed,
            incomplete: business.incomplete,
            progressRate,
            totalAmount: business.totalAmount,
            completedAmount: business.completedAmount,
            incompleteAmount: business.incompleteAmount
          }
        })

        // Sort by business name
        stats.sort((a, b) => a.businessName.localeCompare(b.businessName))

        setBusinessStats(stats)
      }
    } catch (err) {
      console.error('Failed to fetch business data:', err)
      setError('사업별 데이터를 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [selectedWorkspace?.id, selectedListType, departmentId])

  // Fetch data when panel opens or dependencies change
  useEffect(() => {
    if (isOpen) {
      fetchBusinessData()
    }
  }, [isOpen, fetchBusinessData])

  // Currency formatter for budget amounts
  const formatCurrency = (value: number): string => {
    if (isNaN(value)) return '-'
    return new Intl.NumberFormat('ko-KR').format(value) + '원'
  }

  // Progress Percentage Cell Renderer
  const ProgressPercentageRenderer = (params: any) => {
    const ratio = params.value
    if (typeof ratio !== 'number') return '-'

    return (
      <span
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          color: designTokens.colors.gray[6],
          fontWeight: 400
        }}
      >
        {ratio}%
      </span>
    )
  }

  // Column definitions for Business Statistics DataGrid
  const columnDefs: ColDef[] = useMemo(() => {
    const baseStyle = {
      fontFamily: designTokens.typography.fontFamily.sans[0],
      fontSize: designTokens.typography.fontSize['body-medium'][0],
      color: designTokens.colors.gray[6],
    }

    const rightAlignStyle = {
      textAlign: 'right',
      padding: '0 16px',
      ...baseStyle
    }

    if (selectedListType === 'budget') {
      // Budget columns with amount values
      return [
        {
          headerName: '사업명',
          field: 'businessName',
          width: 200,
          minWidth: 150,
          sortable: true,
          filter: true,
          suppressSizeToFit: true,
          cellStyle: baseStyle
        },
        {
          headerName: '목표 예산',
          field: 'totalAmount',
          width: 140,
          minWidth: 120,
          sortable: true,
          filter: true,
          suppressSizeToFit: true,
          cellStyle: rightAlignStyle,
          valueFormatter: (params) => formatCurrency(params.value || 0)
        },
        {
          headerName: '완료 예산',
          field: 'completedAmount',
          width: 140,
          minWidth: 120,
          sortable: true,
          filter: true,
          suppressSizeToFit: true,
          cellStyle: rightAlignStyle,
          valueFormatter: (params) => formatCurrency(params.value || 0)
        },
        {
          headerName: '미완료 예산',
          field: 'incompleteAmount',
          width: 140,
          minWidth: 120,
          sortable: true,
          filter: true,
          suppressSizeToFit: true,
          cellStyle: rightAlignStyle,
          valueFormatter: (params) => formatCurrency(params.value || 0)
        },
        {
          headerName: '진행율',
          field: 'progressRate',
          width: 100,
          minWidth: 80,
          cellRenderer: ProgressPercentageRenderer,
          sortable: true,
          filter: true,
          suppressSizeToFit: true,
          cellStyle: {
            textAlign: 'center',
            padding: '0 16px',
            ...baseStyle
          },
        },
      ]
    } else {
      // Default columns with count values
      return [
        {
          headerName: '사업명',
          field: 'businessName',
          width: 200,
          minWidth: 150,
          sortable: true,
          filter: true,
          suppressSizeToFit: true,
          cellStyle: baseStyle
        },
        {
          headerName: '전체',
          field: 'total',
          width: 100,
          minWidth: 80,
          sortable: true,
          filter: true,
          suppressSizeToFit: true,
          cellStyle: rightAlignStyle,
          valueFormatter: (params) => `${params.value}개`
        },
        {
          headerName: '완료',
          field: 'completed',
          width: 100,
          minWidth: 80,
          sortable: true,
          filter: true,
          suppressSizeToFit: true,
          cellStyle: rightAlignStyle,
          valueFormatter: (params) => `${params.value}개`
        },
        {
          headerName: '미완료',
          field: 'incomplete',
          width: 100,
          minWidth: 80,
          sortable: true,
          filter: true,
          suppressSizeToFit: true,
          cellStyle: rightAlignStyle,
          valueFormatter: (params) => `${params.value}개`
        },
        {
          headerName: '진행율',
          field: 'progressRate',
          width: 100,
          minWidth: 80,
          cellRenderer: ProgressPercentageRenderer,
          sortable: true,
          filter: true,
          suppressSizeToFit: true,
          cellStyle: {
            textAlign: 'center',
            padding: '0 16px',
            ...baseStyle
          },
        },
      ]
    }
  }, [selectedListType])

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={`${businessName} 사업별 상세`}
      width={1000}
    >
      <div
        className="flex flex-col space-y-6"
        style={{
          padding: '24px',
        }}
      >
        {/* Section 1: 기본정보 */}
        <div className="space-y-2">
          <SectionHeader title="기본정보" />
          <div className="space-y-2" style={{ padding: '0px 4px' }}>
            <InfoField
              label="사업명"
              value={businessName || '-'}
            />
            <InfoField
              label="상세 유형"
              value={getTaskTypeLabel()}
            />
          </div>
        </div>

        {/* Section 2: 사업별 통계 (지원되는 탭일 때 표시) */}
        {['task', 'work', 'budget', 'performance'].includes(selectedListType || '') && (
          <div className="space-y-4">
            <SectionHeader title={`사업별 ${getTaskTypeLabel()} 현황`} />

            {error && (
              <div
                className="text-center py-4"
                style={{
                  color: designTokens.colors.error.DEFAULT,
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                }}
              >
                {error}
              </div>
            )}

            {!error && (
              <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
                <DataGrid
                  rowData={isLoading ? [] : businessStats}
                  columnDefs={columnDefs}
                  headerHeight={40}
                  rowHeight={42}
                  height={40 + (20 * 42) + 60 + 2} // header + (10 rows * rowHeight) + pagination height + border
                  pagination={true}
                  paginationPageSize={20}
                  paginationPageSizeSelector={[20, 50, 100]}
                  rowSelection="single"
                  animateRows={true}
                  loading={isLoading}
                  getRowStyle={(params) => {
                    if (params.node.rowIndex! % 2 === 0) {
                      return { backgroundColor: designTokens.colors.gray[0] }
                    }
                    return { backgroundColor: designTokens.colors.white }
                  }}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: false,
                    suppressSizeToFit: true,
                  }}
                  // 범위 선택 및 복사 기능 활성화
                  enableRangeSelection={true}
                  enableCellCopy={true}
                  suppressRowClickSelection={true}
                  rangeCopyFormatter={(colId, value) => {
                    // 진행율 포맷팅
                    if (colId === 'progressRate') {
                      return value !== null && value !== undefined ? `${value}%` : '-'
                    }
                    // 전체/완료/미완료 개수 포맷팅
                    if (colId === 'total' || colId === 'completed' || colId === 'incomplete') {
                      return value !== null && value !== undefined ? `${value}개` : '-'
                    }
                    if (value === null || value === undefined) return '-'
                    return value.toString()
                  }}
                  noRowsOverlayComponent={() => (
                    <div
                      className="flex items-center justify-center h-full"
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: designTokens.typography.fontSize['body-medium'][0],
                        color: designTokens.colors.gray[4],
                      }}
                    >
                      {isLoading ? '데이터를 불러오는 중...' : `사업별 ${getTaskTypeLabel()} 데이터가 없습니다.`}
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {/* Non-supported type message */}
        {!['task', 'work', 'budget', 'performance'].includes(selectedListType || '') && (
          <div className="space-y-4">
            <SectionHeader title={`${getTaskTypeLabel()} 상세 현황`} />
            <div
              className="text-center py-8"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                color: designTokens.colors.gray[4],
              }}
            >
              {getTaskTypeLabel()} 유형의 사업별 상세 현황은 준비 중입니다.
            </div>
          </div>
        )}
      </div>
    </SidePanel>
  )
}

export default BusinessDetailPanel